import { useCallback, useState } from 'react';
import { Icon } from '@iconify/react';
import { useDropzone } from 'react-dropzone';
import { Button, Select, SelectItem, Chip } from '@nextui-org/react';
import { toast } from 'react-toastify';
import { useDocumentUpload } from '../../../hooks/useDocumentUpload';
import { FilePreview } from '../../../components/documents/file-preview';
import { PreviewTable } from '../../../components/tables/preview-table';
import Header from '../../../components/layouts/Header';
import { EmptyState, PageLayout } from '../../../components/ui';
import VolumeUploadPanel from '../../../components/documents/VolumeUploadPanel';

const SuperadminDocuments = () => {
  const [documentType, setDocumentType] = useState<string>('');
  const [monthsParam, setMonthsParam] = useState<string>('4'); // Default a 4 meses
  const [uploadMode, setUploadMode] = useState<'replace' | 'append'>('replace');

  const {
    file,
    setFile,
    previewData,
    columns,
    isUploading,
    uniqueSpeciesIds,
    handleFileRead,
    handleUpload,
    handleCancel,
  } = useDocumentUpload();

  const handleDocumentTypeChange = (value: string) => {
    setDocumentType(value);
    handleCancel();
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!documentType) {
        toast.warning('Por favor selecciona un tipo de documento primero');
        return;
      }
      const file = acceptedFiles[0];
      setFile(file);
      await handleFileRead(file, documentType);
    },
    [documentType]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const handleUploadClick = async () => {
    if (!documentType) {
      toast.warning('Por favor selecciona un tipo de documento');
      return;
    }
    // Pasar el parámetro de meses al método de carga
    const success = await handleUpload(documentType, parseInt(monthsParam), uploadMode);
    if (success) {
      handleCancel();
    }
  };

  // Opciones de meses para borrar datos
  const monthsOptions = [
    { key: '1', label: '1 Mes' },
    { key: '2', label: '2 Meses' },
    { key: '3', label: '3 Meses' },
    { key: '4', label: '4 Meses' },
    { key: '6', label: '6 Meses' },
    { key: '12', label: '1 Año (12 Meses)' },
    { key: '24', label: '2 Años (24 Meses)' },
    { key: '48', label: '4 Años (48 Meses)' },
    { key: '72', label: '6 Años (72 Meses)' },
    { key: '1200', label: 'Sin límite (todo el archivo)' },
  ];

  return (
    <PageLayout>
      <Header title='Recepción de Documentos' />

      <div className='max-w-xl mb-8 flex flex-wrap gap-4'>
        <Select
          label='Tipo de Documento'
          placeholder='Selecciona un tipo de documento'
          value={documentType}
          onChange={(e) => handleDocumentTypeChange(e.target.value)}
          isDisabled={isUploading}
          isRequired
          className='flex-1 min-w-[250px]'
        >
          <SelectItem key='market_data' value='market_data'>
            Market Data
          </SelectItem>
          <SelectItem key='settlements' value='settlements'>
            Liquidaciones
          </SelectItem>
          <SelectItem key='other_data' value='other_data'>
            Otros Datos
          </SelectItem>
          <SelectItem key='volume' value='volume'>
            Volumen industria
          </SelectItem>
        </Select>

        {documentType === 'market_data' && (
          <>
            <Select
              label='Modo de carga'
              selectedKeys={[uploadMode]}
              onChange={(e) =>
                setUploadMode(
                  (e.target.value as 'replace' | 'append') || 'replace'
                )
              }
              isDisabled={isUploading}
              className='flex-1 min-w-[220px]'
              description={
                uploadMode === 'append'
                  ? 'Solo inserta; falla si el período del archivo ya tiene datos.'
                  : 'Sustituye el período de fechas que cubre el archivo.'
              }
            >
              <SelectItem key='replace' value='replace'>
                Reemplazar período del archivo
              </SelectItem>
              <SelectItem key='append' value='append'>
                Solo agregar (período nuevo)
              </SelectItem>
            </Select>
            <Select
              label='Ignorar filas anteriores a'
              value={monthsParam}
              onChange={(e) => setMonthsParam(e.target.value)}
              isDisabled={isUploading}
              className='flex-1 min-w-[200px]'
              description='Filtra el archivo; ya no borra datos de la base.'
            >
              {monthsOptions.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </>
        )}
      </div>

      {documentType === 'volume' ? (
        // Base de volumen: flujo propio (cientos de miles de filas).
        <VolumeUploadPanel />
      ) : !documentType ? (
        <EmptyState
          icon='tabler:file-search'
          title='Selecciona un tipo de documento para continuar'
        />
      ) : !file ? (
        <div className='min-h-[400px] flex flex-col items-center justify-center'>
          <div
            {...getRootProps()}
            className={`w-full max-w-2xl h-64 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-default-300 hover:border-default-400'
            }`}
          >
            <input {...getInputProps()} />
            <div className='text-center'>
              <Icon icon='tabler:upload' width={48} className='text-default-400 mb-4 mx-auto' />
              <p className='text-lg mb-2'>
                {isDragActive
                  ? 'Suelta el archivo aquí'
                  : 'Arrastra y suelta un archivo Excel aquí'}
              </p>
              <p className='text-sm text-default-500'>
                o haz clic para seleccionar
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className='space-y-6'>
          <FilePreview file={file} />

          {documentType === 'market_data' && uniqueSpeciesIds.length > 0 && (
            <div className='flex flex-wrap gap-2 my-4'>
              <div className='mr-2 py-1'>Species detectadas:</div>
              {uniqueSpeciesIds.map((id) => (
                <Chip key={id} variant='flat'>
                  {id}
                </Chip>
              ))}
              <div className='w-full mt-2 text-sm text-default-600'>
                Nota: Solo se eliminarán los datos de estas especies detectadas
                para los últimos{' '}
                {monthsOptions
                  .find((o) => o.key === monthsParam)
                  ?.label.toLowerCase()}
                .
              </div>
            </div>
          )}

          {documentType === 'settlements' && uniqueSpeciesIds.length > 0 && (
            <div className='flex flex-wrap gap-2 my-4'>
              <div className='mr-2 py-1'>Species detectadas:</div>
              {uniqueSpeciesIds.map((id) => (
                <Chip key={id} variant='flat'>
                  {id}
                </Chip>
              ))}
              <div className='w-full mt-2 text-sm text-default-600'>
                Nota: Las liquidaciones se asociarán a estas especies detectadas
                en la columna "ESPECIE" del Excel.
              </div>
            </div>
          )}

          <PreviewTable columns={columns} previewData={previewData} />
          <div className='flex justify-end gap-4'>
            <Button
              variant='light'
              startContent={<Icon icon='tabler:x' />}
              onClick={handleCancel}
              isDisabled={isUploading}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              startContent={<Icon icon='tabler:upload' />}
              onClick={handleUploadClick}
              isDisabled={isUploading}
            >
              Subir a Base de Datos
            </Button>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default SuperadminDocuments;
